// Spiel-Auswahl im Schriftbot-Design: gleiche Typo und Flächen wie die
// Home-Sidebar (dunkles Ink, weiche weiße Flächen, Pill-Buttons).
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { LogIn, LogOut, ChevronLeft, Settings } from "lucide-react";
import { GAMES } from "./registry";
import { useGameAuth, useGameProfile, fetchMyCoins } from "./gamesFirebase";
import CoinCollectBanner from "./CoinCollectBanner";
import UsernameDialog from "./UsernameDialog";
import { drawSprite } from "./pixelArt";

// Breites Pixel-Banner (20x10-Szene) als Spielvorschau.
const PreviewBanner = ({ preview }) => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    drawSprite(ctx, preview.rows, preview.palette, 0, 0, 16);
  }, [preview]);
  return (
    <canvas
      ref={ref}
      width={320}
      height={160}
      className="w-full aspect-[2/1] block"
      style={{ imageRendering: "pixelated" }}
      aria-hidden="true"
    />
  );
};

export default function GamesHub() {
  const { user, login, logout } = useGameAuth();
  const { username, profileReady, saveUsername } = useGameProfile(user);
  const [showSettings, setShowSettings] = useState(false);
  const [coinBalance, setCoinBalance] = useState(0);

  // Gesammelte Münzen fürs Banner laden (nur für Angemeldete relevant)
  useEffect(() => {
    let active = true;
    if (user) {
      fetchMyCoins().then((c) => {
        if (active) setCoinBalance(c);
      });
    } else {
      setCoinBalance(0);
    }
    return () => {
      active = false;
    };
  }, [user]);

  // Nach dem Login sofort einen Username einfordern (Pflicht zum Spielen)
  const needsUsername = user && profileReady && !username;

  return (
    <div className="min-h-screen bg-[#0b0c0f] text-white selection:bg-white selection:text-black">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <header className="flex items-center justify-between gap-4 mb-8 sm:mb-10">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-white/60 hover:text-white transition-colors"
          >
            <ChevronLeft size={16} /> Schriftbot
          </Link>

          {user ? (
            <div className="flex items-center gap-3">
              <span className="text-sm text-white/55 hidden sm:inline">
                {username || ""}
              </span>
              <button
                onClick={() => setShowSettings(true)}
                aria-label="Einstellungen"
                className="glass-accent inline-flex items-center justify-center w-9 h-9 rounded-full"
              >
                <Settings size={15} />
              </button>
              <button
                onClick={logout}
                className="glass-accent inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
              >
                <LogOut size={14} /> Logout
              </button>
            </div>
          ) : (
            <button
              onClick={login}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-zinc-900 text-sm font-bold hover:opacity-90 transition-opacity"
            >
              <LogIn size={14} /> Anmelden
            </button>
          )}
        </header>

        {/* Titel — wie der Home-Sidebar-Header */}
        <div className="flex flex-col gap-1 mb-6 sm:mb-8">
          <h1 className="text-2xl font-bold tracking-tight text-white">
            Schriftbot Games
          </h1>
          <p className="text-xs text-white/55 font-medium uppercase tracking-widest">
            Kurze Spiele mit weltweiter Bestenliste
          </p>
          <CoinCollectBanner coins={coinBalance} showCollected={Boolean(user)} />
          {!user && (
            <p className="mt-2 text-sm text-white/60 leading-relaxed">
              Melde dich an und wähle einen Username, um zu spielen und in den
              Ranglisten zu erscheinen.
            </p>
          )}
        </div>

        {/* Spiel-Karten */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {GAMES.map((game) => (
            <Link
              key={game.id}
              to={`/games/${game.id}`}
              className="group bg-white/[0.04] border border-white/10 rounded-2xl overflow-hidden hover:bg-white/[0.06] hover:border-white/25 transition-colors"
            >
              <PreviewBanner preview={game.preview} />
              <div className="p-4 sm:p-5">
                <div className="flex items-baseline justify-between gap-3">
                  <h2 className="text-lg font-bold tracking-tight">
                    {game.name}
                  </h2>
                  <span className="text-sm font-bold text-white/60 group-hover:text-white transition-colors whitespace-nowrap">
                    Spielen →
                  </span>
                </div>
                <p className="mt-1 text-sm text-white/60 leading-relaxed">
                  {game.desc}
                </p>
              </div>
            </Link>
          ))}
        </div>

        <footer className="mt-12 pt-5 border-t border-white/10 flex flex-wrap gap-x-6 gap-y-2 text-xs text-white/40">
          <Link
            to="/impressum"
            className="hover:text-white/70 transition-colors"
          >
            Impressum
          </Link>
          <Link
            to="/datenschutz"
            className="hover:text-white/70 transition-colors"
          >
            Datenschutz
          </Link>
        </footer>
      </div>

      {(needsUsername || showSettings) && (
        <UsernameDialog
          initial={username}
          onSave={saveUsername}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
}
